import React, { useEffect, useState } from "react";
import ReusableBreadcrumbs from "../../CommonComponents/ReusableBreadCrumbs";
import { useParams, useSearchParams } from "react-router-dom";
import { nanoid } from "nanoid";
import InputDropdown from "../../CommonComponents/InputDropdown";
import useAxiosPrivate from "../../../Hooks/useAxiosPrivate";
import useAuth from "../../../Hooks/useAuth";
import InputField from "../../CommonComponents/InputField";
import Button from "../../Button";
import { libTypeIds, RESULT_STATUS, Roles } from "../../../constants";
import useS3FileManagement from "../../../Hooks/useS3FileManagement";
import Loading from "../../Notification-Loading/Loading/Loading";
import { getDataFromStorage } from "../../../util";
import RichTextEditor from "../../CommonComponents/RichTextEditor";
import EmbeddedLink from "../Assignment/EmbeddedLink";
import DragAndDropUpload from "../../CommonComponents/DragAndDropUpload";
import Checkbox from "../../CommonComponents/Checkbox";
import Tooltip from "../../CommonComponents/Tooltip";

const validators = {
  topicid: (v) => (!v ? "Topic is required" : ""),
  name: (v) => (!v ? "Name is required" : ""),
  maxtime: (v) =>
    !v || !Number.isInteger(+v)
      ? "Max time is required"
      : +v <= 0
      ? "Max time must be greater than 0"
      : "",
};
const supportedFormat = ".zip, .jpeg, .jpg, .png, .pdf",
  supportMimeTypes = [
    "application/zip",
    "application/png",
    "application/x-zip-compressed",
    "image/png",
    "image/jpg",
    "image/apng",
    "image/jpeg",
  ];

const ContentEntry = () => {
  const { type, questionid: libid } = useParams();
  const axios = useAxiosPrivate();
  const { setIsLoaded, isLoaded, navigate, setShowNotify } = useAuth();
  const [searchParms] = useSearchParams();

  const selectable = searchParms.get("selectable") === "true";
  const lpid = searchParms.get("lpId") || null;
  const moduleId = searchParms.get("moduleId") || null;
  const chapterId = searchParms.get("chapterId") || null;
  const batchId = searchParms.get("bId") || null;

  let queryString = `${
    selectable
      ? `?selectable=${selectable}${lpid ? `&lpId=${lpid}` : ""}${
          moduleId ? `&moduleId=${moduleId}` : ""
        }${chapterId ? `&chapterId=${chapterId}` : ""}${
          batchId ? `&bId=${batchId}` : ""
        }`
      : ""
  }`;

  const baseRoute = batchId ? `/batches/${batchId}/courses` : "/learningpath";
  const { onMultiPartUpload, getPreSignedUrl } = useS3FileManagement();

  const isEdit = Number.isInteger(+libid);

  const items = [
    {
      label: "Library",
    },
    {
      label: "Contents",
      href: `/library/${type}/questionlist`,
    },
    {
      label: "Add Content",
    },
  ];

  const [topicOptions, setTopicOptions] = useState([]);
  const [contentDetails, setContentDetails] = useState({
    contentType: 0,
    name: "",
    topicid: null,
    maxtime: null,
  });
  const [errors, setErrors] = useState({});
  const [newUploadedFiles, setNewUploadedFiles] = useState([]);

  useEffect(() => {
    const getTopicsAndContent = async () => {
      try {
        setIsLoaded(true);
        const [topicRes, contentRes] = await Promise.all([
          axios.get(`node/admin/topic/get/${0}`),
          isEdit ? axios.get(`node/admin/content/${libid}`) : null,
        ]);
        console.log("contentRes", contentRes);
        if (isEdit) {
          if (contentRes.data.resultCode === RESULT_STATUS.SUCCESS) {
            setContentDetails({
              ...contentRes.data.data,
              contentType: contentRes.data.data.videoContent,
              maxtime: contentRes.data.data.timelimit,
              link: contentRes.data.data.link.map((item) => ({
                ...item,
                name: item.link?.split("/")?.pop(),
                status: "old",
                id: nanoid(),
              })),
            });
            setNewUploadedFiles([]);
          } else {
            setShowNotify({
              show: true,
              title: "Error !",
              msg: `Unable to get content details`,
            });
          }
        }
        if(topicRes.data.resultCode === RESULT_STATUS.SUCCESS)
          setTopicOptions(
            topicRes.data?.data?.map((item) => ({
              ...item,
              label: item.name,
              value: item.id,
            })) || [],
          );
      } catch (error) {
        setShowNotify({
          show: true,
          title: error.message.includes("403") ? "Warning !" : "Error !",
          msg: error.message.includes("403")
            ? "You have been logged-out due to inactivity. Login again."
            : isEdit
            ? `Unable to get topic or content details`
            : `Unable to get the topic`,
          isUnAuth: error.message.includes("403"),
        });
      } finally {
        setIsLoaded(false);
      }
    };
    getTopicsAndContent();
  }, [axios, setIsLoaded, setShowNotify, libid, isEdit]);

  const onChange = (field, value) => {
    const validator = validators[field];
    if (validator) {
      const error = validator(value);
      setErrors((prev) => ({ ...prev, [field]: error }));
    }
    setContentDetails((prev) => ({ ...prev, [field]: value }));
  };

  const onClickBack = () => {
    if (lpid) {
      navigate(
        `${baseRoute}/${lpid}/module/${moduleId}?chapterId=${chapterId}`,
      );
    } else {
      navigate(`/library/${type}/questionlist`);
    }
  };

  const onDragOver = (event) => event.preventDefault();

  const onDrop = (event) => {
    const files = Array.from(event.dataTransfer.files);
    uploadFiles(files);
  };

  const uploadFiles = (files = []) => {
    // if (files.length > 1) {
    //   setErrors({
    //     ...errors,
    //     attachment:
    //       "Multiple files are not supported in this format. Please upload a single ZIP file.",
    //   });
    //   return;
    // }
    const isUploadableFormat = files.every((file) =>
      supportMimeTypes.includes(file.type),
    );
    if (!isUploadableFormat) {
      setErrors({
        ...errors,
        attachment: `Unsupported file type: ${
          files[0].type.split("/")[1]
        }. Allowed formats are: ${supportedFormat}`,
      });
      return;
    }
    // setDetails({ ...details, attachment: files[0] });
    setNewUploadedFiles((prev) => [...prev, ...files]);
    setContentDetails((pre) => {
      let copy = structuredClone(pre);
      let newLinks = files.map((file) => ({
        ...file,
        link: `library/content/${file.name}`,
        name: file.name,
        status: "new",
        checked: 0,
        id: nanoid(),
      }));
      if (!copy.link) copy.link = [];
      copy.link?.push(...newLinks);
      return copy;
    });
    setErrors({ ...errors, attachment: "" });
  };

  const onUpload = (event) => {
    const files = Array.from(event.target.files);
    uploadFiles(files);
    event.target.value = "";
  };

  const onOpenUploadedFile = async (file) => {
    if (file.status === "old") {
      const { url } = await getPreSignedUrl(
        file?.link,
        undefined,
        file?.link?.split(".")?.pop(),
        "GET",
      );
      window.open(url, "_blank", "noreferrer");
    } else {
      let [newFile] = newUploadedFiles.filter(
        (item) => item.name === file.name,
      );
      let url = URL.createObjectURL(newFile);
      window.open(url, "_blank", "noreferrer");
    }
  };

  const onDeleteLink = (id) => {
    setContentDetails((prev) => ({
      ...prev,
      link: prev.link?.filter((item) => item.id !== id),
    }));
    setNewUploadedFiles((prev) => prev.filter((item) => item.id !== id));
  };

  const onSaveOrUpdate = async (from) => {
    let copyContentDetails = structuredClone(contentDetails);
    copyContentDetails.topic = copyContentDetails.topicid;
    if (typeof copyContentDetails.topicid === "string")
      copyContentDetails.topicname = copyContentDetails.topicid;
    try {
      setIsLoaded(true);
      let form = new FormData();

      let fileNames = [],
        candown = [];

      if (isEdit) {
        copyContentDetails.id = libid;
      }

      form.append("qdata", JSON.stringify({ qdata: copyContentDetails }));
      form.append("libtypeid", type);
      form.append("libtypename", "content");
      if (copyContentDetails.embedLinks?.length > 0) {
        form.append("embedLinks", copyContentDetails.embedLinks);
      }

      if (newUploadedFiles.length > 0) {
        form.append("hasfiles", "yes");
        for (const file of newUploadedFiles) {
          fileNames.push(file.name);
          candown.push({
            [file.name]: file.checked,
          });

          let filePath = "library/content/" + file.name;
          await onMultiPartUpload(filePath, file);
        }
      }
      form.append("isfiledownloadable", JSON.stringify(candown));
      form.append("fileNames", JSON.stringify(fileNames));
      form.append(
        "contentType",
        parseInt(copyContentDetails.contentType) === 0
          ? "content-editor"
          : "video-attachment",
      );
      if (copyContentDetails.link?.length > 0) {
        form.append("links", JSON.stringify(copyContentDetails.link));
      }

      const { data: libRes } = await axios.post(
        "node/admin/library/add",
        form,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );
      if (libRes.resultCode === RESULT_STATUS.SUCCESS) {
        console.log(" libRes ", libRes);

        if (lpid && !isEdit) {
          let tests = [libRes.data.libid];
          const { data } = await axios.post(
            `node/admin/learningpath/${lpid}/module/${moduleId}/assign-tests`,
            { type: libTypeIds.CONTENT, tests, chapterId },
          );

          if (data.resultCode === RESULT_STATUS.SUCCESS) {
            navigate(`${baseRoute}/${lpid}/module/${moduleId}`);
          } else {
            navigate(`/library/${libTypeIds.CONTENT}/testlist${queryString}`);
            throw new Error(`The content is created but unable to add to path`);
          }
        } else if(lpid && isEdit){
           navigate(`${baseRoute}/${lpid}/module/${moduleId}`);
        } else {
          // setResultCode(res.data.resultCode);
          navigate(`/library/${libTypeIds.CONTENT}/questionlist`);
          setShowNotify({
            show: true,
            title: "Info",
            msg: libRes.message,
          });
        }
      } else {
        setShowNotify({
          show: true,
          title: "Error !",
          msg: libRes.message,
        });
      }
    } catch (error) {
      console.error("Invalid description JSON:", error);
      setShowNotify({
        show: true,
        title: error.message.includes("403") ? "Warning !" : "Error !",
        msg: error.message.includes("403")
          ? "You have been logged-out due to inactivity. Login again."
          : error.message ??
            `Unable to ${isEdit ? "update" : "create"} the content`,
        isUnAuth: error.message.includes("403"),
      });
    } finally {
      setIsLoaded(false);
    }
  };

  return (
    <div className="content-entry-container">
      <div className="content-entry-header-wrapper">
        <ReusableBreadcrumbs items={items} />
        <div
          className={`content-entry-header-back-btn-wrapper`}
          onClick={onClickBack}
        >
          <svg
            width="7"
            height="12"
            viewBox="0 0 7 12"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M5.7998 10.8L0.799805 5.80005L5.7998 0.800049"
              stroke="#667085"
              strokeWidth="1.6"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <p className="content-entry-header-back-btn-text">Back</p>
        </div>

        <h2 className="content-entry-header">
          {isEdit ? "Edit" : "Add"} Content
        </h2>
      </div>
      <div className="content-entry-form">
        <div className="content-entry-form-wrapper">
          <InputDropdown
            options={topicOptions}
            value={contentDetails.topicid}
            placeholder="Select or create topic"
            creatable
            onChange={(value) => onChange("topicid", value)}
            label="Topic"
            hintText={errors.topicid}
            destructive={!!errors.topicid}
          />
          <InputField
            name="name"
            type="text"
            value={contentDetails.name}
            label="Name"
            onChange={(event) =>
              onChange(event.target.name, event.target.value)
            }
            hintText={errors.name}
            destructive={!!errors.name}
          />
          <RichTextEditor
            title="Description"
            name="description"
            value={contentDetails.description}
            onChange={(value) => onChange("description", value)}
          />
          <EmbeddedLink
            links={contentDetails.embedLinks}
            onAdd={(value) =>
              setContentDetails((prev) => ({
                ...prev,
                embedLinks: [...(prev.embedLinks ?? []), value],
              }))
            }
            onDelete={(index) =>
              setContentDetails((prev) => ({
                ...prev,
                embedLinks: prev.embedLinks.filter((_, i) => i !== index),
              }))
            }
          />
          <div className="content-entry-form-attachment-wrapper">
            <DragAndDropUpload
              label="Add your attachment"
              text="Drop you attachment here"
              onDragOver={onDragOver}
              onDrop={onDrop}
              onUpload={onUpload}
              supportFormat={supportedFormat}
              hintText={errors.attachment}
              destructive={!!errors.attachment}
            />
            {contentDetails.link?.length ? (
              <div className="uploaded-files-container">
                {contentDetails.link?.map((item, index) => (
                  <UploadedFile
                    key={index}
                    file={item}
                    index={index}
                    onChange={(id, value) =>
                      setContentDetails((prev) => ({
                        ...prev,
                        link: prev.link?.map((item, linkIndex) => {
                          if (item.id === id) item.checked = value;
                          return item;
                        }),
                      }))
                    }
                    onDelete={(id) => onDeleteLink(id)}
                    onClick={onOpenUploadedFile}
                  />
                ))}
              </div>
            ) : null}
          </div>
          <InputField
            name="maxtime"
            type="number"
            value={contentDetails.maxtime}
            label="Max Time(min/s)"
            placeholder="Enter the max time"
            onChange={(event) =>
              onChange(event.target.name, event.target.value)
            }
            hintText={errors.maxtime}
            destructive={!!errors.maxtime}
          />
        </div>
        {getDataFromStorage("role") !== Roles.tutor ? (
          <>
            <div className="content-entry-form-btn-wrapper content-entry-form-old-btn-wrapper">
              <Button
                size="sm"
                className="content-entry-form-old-save-btn"
                hierarchy={{
                  type: "primary",
                  buttonText: `${isEdit ? "Update" : "Save"}`,
                }}
                onClick={() => onSaveOrUpdate(false)}
              />
            </div>
            {console.log("ROLE:", getDataFromStorage("role"))}
          </>
        ): null}
      </div>

      {isLoaded && <Loading />}
    </div>
  );
};

const UploadedFile = ({
  index,
  file = {},
  onDelete = null,
  onChange = null,
  onClick = null,
}) => {
  const onChangeDownloadable = (index, value) => {
    onChange?.(index, value);
  };

  const onDeleteLink = (event, index) => {
    event.stopPropagation();
    onDelete?.(index);
  };

  return (
    <div className="uploaded-file-container" onClick={() => onClick?.(file)}>
      <div className="uploaded-file-info-wrapper">
        <div className="uploaded-file-info">
          <svg
            width="18"
            height="22"
            viewBox="0 0 18 22"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M10 1H3C2.46957 1 1.96086 1.21071 1.58579 1.58579C1.21071 1.96086 1 2.46957 1 3V19C1 19.5304 1.21071 20.0391 1.58579 20.4142C1.96086 20.7893 2.46957 21 3 21H15C15.5304 21 16.0391 20.7893 16.4142 20.4142C16.7893 20.0391 17 19.5304 17 19V8M10 1L17 8M10 1V8H17"
              stroke="#344054"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <p className="uploaded-file-info-title">{file.name}</p>
        </div>
        <Tooltip
          isIcon
          title="Delete"
          className="uploaded-file-info-delete-btn"
        >
          <svg
            width="13"
            height="15"
            viewBox="0 0 13 15"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            onClick={(event) => onDeleteLink(event, file.id)}
          >
            <path
              d="M0.5 3.16667H1.83333M1.83333 3.16667H12.5M1.83333 3.16667V12.5C1.83333 12.8536 1.97381 13.1928 2.22386 13.4428C2.47391 13.6929 2.81304 13.8333 3.16667 13.8333H9.83333C10.187 13.8333 10.5261 13.6929 10.7761 13.4428C11.0262 13.1928 11.1667 12.8536 11.1667 12.5V3.16667H1.83333ZM3.83333 3.16667V1.83333C3.83333 1.47971 3.97381 1.14057 4.22386 0.890524C4.47391 0.640476 4.81304 0.5 5.16667 0.5H7.83333C8.18696 0.5 8.52609 0.640476 8.77614 0.890524C9.02619 1.14057 9.16667 1.47971 9.16667 1.83333V3.16667M5.16667 6.5V10.5M7.83333 6.5V10.5"
              stroke="#F04438"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </Tooltip>
      </div>
      <Checkbox
        text="Attachment Downloadable"
        checked={file.checked}
        onChange={(value) => onChangeDownloadable(file.id, value)}
        className="uploaded-link-downloadable-checkbox"
      />
    </div>
  );
};

export default ContentEntry;
